import {
  HHButton,
  HHChip,
  HHStack,
  TextCopier,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useFilterUrlSync } from '../../../hooks/use-filter-url-sync';
import {
  useGetAggregateHoldsV2LazyQuery,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetContractIdsLazyQuery,
  useGetContractsV2LazyQuery,
  useGetPartnershipsQuery,
} from '../../../types';
import { formatCurrency } from '../../../utils/currency-helpers';
import { displayDateTime } from '../../../utils/date-helper';
import { GridFooter } from '../../grid-components/grid-footer';
import { NoRowsOverlay } from '../../grid-components/no-rows-or-error-overlay';
import { useGridPagination } from '../../grid-components/use-grid-pagination';
import { buildBillManagementUrl } from '../bill-management/bill-management-navigator';
import { getGridCount } from '../utils';
import {
  AggregateClientCell,
  AggregateHoldTypeCell,
} from './aggregate-holds-cell';
import {
  AggregateGridColumnFields,
  AggregateGridColumnHeaderNames,
  AggregateHoldDataType,
  AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS,
  AGGREGATE_HOLDS_DEFAULT_PAGE_SIZE,
  defaultAggregateHoldsGridFilterModel,
  DEFAULT_HOLD_TYPES,
} from './aggregate-holds-types';
import AggregateHoldsFilters from './filters';
import { generateEnrichedAggregateHoldsData } from './utils';

export const AGGREGATE_HOLDS_VIEW_HOLDS_BUTTON_TEST_ID = (
  rowId: number,
): string => `aggregate-holds-${rowId}-view-holds-button`;

const AggregateHoldsGrid = (): JSX.Element => {
  const defaultFromDate = dayjs()
    .subtract(AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS, 'day')
    .format('YYYY-MM-DD');
  const defaultToDate = dayjs().format('YYYY-MM-DD');

  const [filterParams, setFilterParams] = useFilterUrlSync(
    defaultAggregateHoldsGridFilterModel,
  );

  const {
    paginationModel,
    setPaginationModel,
    handlePaginationChange,
    handlePageOptionChange,
  } = useGridPagination({
    page: 0,
    pageSize: AGGREGATE_HOLDS_DEFAULT_PAGE_SIZE,
  });

  const {
    data: clientsData,
    error: clientsError,
    loading: clientsLoading,
  } = useGetAllClientsQuery();

  const {
    data: insurersData,
    error: insurersError,
    loading: insurersLoading,
  } = useGetAllInsurersQuery();

  const {
    data: partnershipsData,
    error: partnershipsError,
    loading: partnershipsLoading,
  } = useGetPartnershipsQuery({ variables: { type: 'billing' } });

  const [gridRows, setGridRows] = useState<GridRowsProp>([]);

  const [
    getAggregateHolds,
    {
      data: aggregateHoldsData,
      loading: aggregateLoading,
      error: aggregateError,
    },
  ] = useGetAggregateHoldsV2LazyQuery();

  const [
    getContractIds,
    { loading: contractIdsLoading, error: contractIdsError },
  ] = useGetContractIdsLazyQuery();

  const [getContracts, { data: contractsData, loading: contractsLoading }] =
    useGetContractsV2LazyQuery();

  const getViewHoldsUrl = useCallback(
    (row: AggregateHoldDataType): string =>
      buildBillManagementUrl({
        clientIds: [row.client.id],
        insurerIds: [row.insurer.id],
        held: true,
        // The aggregate window filters when a hold was placed, not the bill's
        // billing period, so the row's own period range is the only range that
        // contains its held bills.
        startDate: row.minFromDate,
        endDate: row.maxFromDate,
        holdReasons: row.holdType.split(',').map(r => r.trim()),
        partnershipIds: row.partnership.id ? [row.partnership.id] : [],
      }),
    [],
  );

  const handleViewHolds = useCallback(
    (row: AggregateHoldDataType) => () => {
      window.open(getViewHoldsUrl(row), '_blank');
    },
    [getViewHoldsUrl],
  );

  const fetchAggregateHolds = useCallback(
    (contractIds: number[]) => {
      getAggregateHolds({
        variables: {
          contractIds,
          holdTypes:
            filterParams.holdTypes.length > 0
              ? filterParams.holdTypes
              : DEFAULT_HOLD_TYPES,
          fromDate: filterParams.startDate || defaultFromDate,
          toDate: filterParams.endDate || defaultToDate,
          minHeldAmount: filterParams.minTotalImpact || 0,
          page: paginationModel.page + 1,
          size: paginationModel.pageSize,
        },
      }).then(response => {
        const returnedContractIds =
          response.data?.aggregateHoldsV2?.items?.map(
            hold => hold.contractId,
          ) ?? [];
        if (returnedContractIds.length > 0) {
          getContracts({
            variables: {
              contractIds: returnedContractIds,
              page: 1,
              size: returnedContractIds.length,
            },
          });
        }
      });
    },
    [
      getAggregateHolds,
      getContracts,
      filterParams.holdTypes,
      filterParams.startDate,
      filterParams.endDate,
      filterParams.minTotalImpact,
      defaultFromDate,
      defaultToDate,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  useEffect(() => {
    const hasEntityFilters =
      filterParams.clientIds.length > 0 ||
      filterParams.insurerIds.length > 0 ||
      filterParams.partnershipIds.length > 0;
    if (!hasEntityFilters) {
      // No client/insurer/partnership filter → all holds (no contract scope).
      fetchAggregateHolds([]);
      return;
    }

    getContractIds({
      variables: {
        clientIds: filterParams.clientIds,
        insurerIds: filterParams.insurerIds,
        partnershipIds: filterParams.partnershipIds,
      },
    }).then(response => {
      if (
        !response.data ||
        response.error ||
        response.data.getContractIds.length === 0
      ) {
        setGridRows([]);
        return;
      }
      fetchAggregateHolds(response.data.getContractIds);
    });
  }, [filterParams, getContractIds, fetchAggregateHolds]);

  useEffect(() => {
    if (aggregateHoldsData?.aggregateHoldsV2?.items && !aggregateLoading) {
      const aggregateHolds = aggregateHoldsData.aggregateHoldsV2.items;

      const processedRows = generateEnrichedAggregateHoldsData(
        aggregateHolds,
        contractsData?.getContractsV2?.items ?? [],
        clientsData?.getAllClients ?? [],
        insurersData?.getAllInsurers ?? [],
        partnershipsData?.getPartnerships ?? [],
      );
      setGridRows(processedRows);
    } else if (
      aggregateLoading === false &&
      !aggregateHoldsData?.aggregateHoldsV2?.items
    ) {
      setGridRows([]);
    }
  }, [
    aggregateHoldsData,
    aggregateLoading,
    contractsData,
    clientsData,
    insurersData,
    partnershipsData,
  ]);

  const getDollarImpactChipColor = (
    amount: number,
  ): 'error' | 'warning' | 'default' => {
    if (amount > 200000) {
      return 'error';
    }
    if (amount > 100000) {
      return 'warning';
    }
    return 'default';
  };

  const columns: GridColDef[] = [
    {
      field: AggregateGridColumnFields.CLIENT,
      headerName: AggregateGridColumnHeaderNames.CLIENT,
      flex: 1.5,
      minWidth: 180,
      resizable: true,
      disableColumnMenu: true,
      renderCell: params => <AggregateClientCell client={params.value} />,
    },
    {
      field: AggregateGridColumnFields.INSURER,
      headerName: AggregateGridColumnHeaderNames.INSURER,
      flex: 1.5,
      minWidth: 200,
      resizable: true,
      disableColumnMenu: true,
      valueFormatter: (params): string => params.value?.name || '--',
    },
    {
      field: AggregateGridColumnFields.PARTNERSHIP,
      headerName: AggregateGridColumnHeaderNames.PARTNERSHIP,
      flex: 1,
      minWidth: 100,
      resizable: true,
      disableColumnMenu: true,
      valueFormatter: (params): string => params.value?.name || '--',
    },
    {
      field: AggregateGridColumnFields.CONTRACT_DATE_RANGE,
      headerName: AggregateGridColumnHeaderNames.CONTRACT_DATE_RANGE,
      flex: 1,
      minWidth: 160,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: (params): string => params.value || '--',
    },
    {
      field: AggregateGridColumnFields.LAST_SEEN,
      headerName: AggregateGridColumnHeaderNames.LAST_SEEN,
      width: 100,
      disableColumnMenu: true,
      valueFormatter: (params): string =>
        params.value
          ? displayDateTime(params.value, { format: 'L', addTimezone: false })
          : '--',
    },
    {
      field: AggregateGridColumnFields.HOLD_TYPE,
      headerName: AggregateGridColumnHeaderNames.HOLD_TYPE,
      width: 130,
      disableColumnMenu: true,
      renderCell: params => <AggregateHoldTypeCell holdTypes={params.value} />,
    },
    {
      field: AggregateGridColumnFields.TOTAL_HOLDS,
      headerName: AggregateGridColumnHeaderNames.TOTAL_HOLDS,
      width: 100,
      disableColumnMenu: true,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: AggregateGridColumnFields.TOTAL_IMPACT,
      headerName: AggregateGridColumnHeaderNames.TOTAL_IMPACT,
      width: 115,
      disableColumnMenu: true,
      renderCell: (params): JSX.Element => {
        const amount = params.value;
        const chipColor = getDollarImpactChipColor(amount);
        const formattedAmount = formatCurrency(amount, 'USD');

        return (
          <HHChip
            hhVariant="variant-bypass"
            color={chipColor}
            size="small"
            label={formattedAmount}
          />
        );
      },
    },
    {
      field: AggregateGridColumnFields.ACTIONS,
      headerName: '',
      width: 165,
      disableColumnMenu: true,
      sortable: false,
      renderCell: params => (
        <HHStack direction="row" spacing={0.5} alignItems="center">
          <HHButton
            size="small"
            variant="outlined"
            hhVariant="variant-bypass"
            data-testid={AGGREGATE_HOLDS_VIEW_HOLDS_BUTTON_TEST_ID(
              params.row.id,
            )}
            onClick={handleViewHolds(params.row)}
          >
            View Holds
          </HHButton>
          <TextCopier
            text={`${window.location.origin}${getViewHoldsUrl(params.row)}`}
            displayText={false}
            alwaysDisplayIcon={true}
            tooltipText="Copy URL to clipboard"
            copiedToolTipText="URL copied!"
          />
        </HHStack>
      ),
    },
  ];

  return (
    <HHStack
      flex={1}
      minHeight="0"
      direction="column"
      spacing={6}
      justifyContent="space-between"
    >
      <AggregateHoldsFilters
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        setPaginationModel={setPaginationModel}
        clients={clientsData?.getAllClients ?? []}
        clientsError={clientsError}
        clientsLoading={clientsLoading}
        insurers={insurersData?.getAllInsurers ?? []}
        insurersError={insurersError}
        insurersLoading={insurersLoading}
        partnerships={partnershipsData?.getPartnerships ?? []}
        partnershipsError={partnershipsError}
        partnershipsLoading={partnershipsLoading}
      />
      <HHStack direction="column" flex={1} minHeight="0">
        <DataGridPro
          aria-label="Aggregate holds table"
          sx={{ flex: 1, width: '100%' }}
          columns={columns}
          rows={gridRows}
          loading={aggregateLoading || contractIdsLoading || contractsLoading}
          // Virtualization is disabled only in tests so all rows render for
          // assertions; in the app it stays on so the fixed-height grid scrolls
          // as a window with the footer pinned below.
          {...(process.env.NODE_ENV === 'test' && {
            disableVirtualization: true,
          })}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={getGridCount(gridRows.length, paginationModel)}
          hideFooter
          slots={{
            noResultsOverlay: NoRowsOverlay,
            noRowsOverlay: NoRowsOverlay,
          }}
          slotProps={{
            noResultsOverlay: {
              error: !!aggregateError || !!contractIdsError,
            } as Record<string, boolean>,
            noRowsOverlay: {
              error: !!aggregateError || !!contractIdsError,
            } as Record<string, boolean>,
          }}
        />

        <GridFooter
          page={paginationModel.page}
          rowsPerPage={paginationModel.pageSize}
          count={getGridCount(gridRows.length, paginationModel)}
          onPageChange={handlePaginationChange}
          onRowsPerPageChange={handlePageOptionChange}
        />
      </HHStack>
    </HHStack>
  );
};

export default AggregateHoldsGrid;
